import React, { useState } from 'react';
import {
    Box,
    Button,
    Menu,
    MenuItem,
    Popover,
    Typography,
    Slider,
    TextField,
    Select,
    FormControl,
    InputLabel,
    Chip,
} from '@mui/material';
import { BoltOutlined } from '@mui/icons-material';
import { vortxColors, retroColors, vortxTypography } from '@vortx/shared-ui';

// Event type definitions with their configurable parameters
const EVENT_TYPES = [
    {
        type: 'cloud_cover',
        label: 'Cloud Cover',
        fields: [
            { key: 'severity_pct', label: 'Severity (%)', kind: 'slider', min: 0, max: 100, step: 5, initial: 50 },
            { key: 'duration_steps', label: 'Duration (steps)', kind: 'number', min: 1, max: 96, initial: 4 },
        ],
    },
    {
        type: 'load_spike',
        label: 'Load Spike',
        fields: [
            { key: 'magnitude_kw', label: 'Magnitude (kW)', kind: 'number', min: 1, max: 5000, initial: 200 },
            { key: 'duration_steps', label: 'Duration (steps)', kind: 'number', min: 1, max: 96, initial: 4 },
        ],
    },
    {
        type: 'price_spike',
        label: 'Price Spike',
        fields: [
            { key: 'multiplier', label: 'Multiplier', kind: 'presets', options: [2, 3, 5], initial: 2 },
            { key: 'duration_steps', label: 'Duration (steps)', kind: 'number', min: 1, max: 96, initial: 4 },
        ],
    },
    {
        type: 'temperature_extreme',
        label: 'Temperature Extreme',
        fields: [
            { key: 'temperature_f', label: 'Temperature (F)', kind: 'number', min: -20, max: 130, initial: 105 },
            { key: 'duration_steps', label: 'Duration (steps)', kind: 'number', min: 1, max: 96, initial: 8 },
        ],
    },
    {
        type: 'equipment_trip',
        label: 'Equipment Trip',
        fields: [
            {
                key: 'equipment',
                label: 'Equipment',
                kind: 'select',
                options: [
                    { value: 'solar', label: 'Solar' },
                    { value: 'battery', label: 'Battery' },
                    { value: 'ev_fleet', label: 'EV Fleet' },
                    { value: 'cold_storage', label: 'Cold Storage' },
                ],
                initial: 'solar',
            },
            { key: 'duration_steps', label: 'Duration (steps)', kind: 'number', min: 1, max: 192, initial: 8 },
        ],
    },
    {
        type: 'vehicle_early_arrival',
        label: 'Vehicle Early Arrival',
        fields: [
            { key: 'vehicle_count', label: 'Vehicle count', kind: 'number', min: 1, max: 50, initial: 5 },
        ],
    },
];

/**
 * EventConfigPopover - renders the config fields for a selected event type
 * and fires onInject with the assembled payload.
 */
function EventConfigPopover({ eventDef, anchorEl, onClose, onInject }) {
    // Initialize field values from definitions
    const [values, setValues] = useState(() => {
        const init = {};
        eventDef.fields.forEach((f) => {
            init[f.key] = f.initial;
        });
        return init;
    });

    const handleChange = (key, val) => {
        setValues((prev) => ({ ...prev, [key]: val }));
    };

    const handleInject = () => {
        const payload = { type: eventDef.type };
        eventDef.fields.forEach((f) => {
            payload[f.key] = values[f.key];
        });
        onInject(payload);
        onClose();
    };

    const fieldSx = {
        '& .MuiInputBase-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
            color: vortxColors.white,
            backgroundColor: retroColors.input,
        },
        '& .MuiInputLabel-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.7rem',
            color: retroColors.muted,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${vortxColors.teal}66`,
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: vortxColors.teal,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: vortxColors.gold,
        },
    };

    return (
        <Popover
            open
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
                sx: {
                    backgroundColor: retroColors.surface,
                    border: `1px solid ${vortxColors.teal}66`,
                    boxShadow: `0 0 20px ${vortxColors.teal}22`,
                    p: 2,
                    minWidth: 260,
                },
            }}
        >
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                fontWeight: 700,
                color: vortxColors.white,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 2,
            }}>
                {eventDef.label}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {eventDef.fields.map((field) => {
                    switch (field.kind) {
                        case 'slider':
                            return (
                                <Box key={field.key}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.65rem',
                                        color: retroColors.muted,
                                        mb: 0.5,
                                    }}>
                                        {field.label}: {values[field.key]}%
                                    </Typography>
                                    <Slider
                                        value={values[field.key]}
                                        onChange={(_e, v) => handleChange(field.key, v)}
                                        min={field.min}
                                        max={field.max}
                                        step={field.step}
                                        sx={{
                                            color: retroColors.accent,
                                            '& .MuiSlider-thumb': { width: 12, height: 12 },
                                            '& .MuiSlider-track': { height: 3 },
                                            '& .MuiSlider-rail': { height: 3, backgroundColor: `${vortxColors.teal}44` },
                                        }}
                                    />
                                </Box>
                            );

                        case 'number':
                            return (
                                <TextField
                                    key={field.key}
                                    label={field.label}
                                    type="number"
                                    size="small"
                                    value={values[field.key]}
                                    onChange={(e) => handleChange(field.key, Number(e.target.value))}
                                    inputProps={{ min: field.min, max: field.max }}
                                    sx={fieldSx}
                                />
                            );

                        case 'presets':
                            return (
                                <Box key={field.key}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.65rem',
                                        color: retroColors.muted,
                                        mb: 0.5,
                                    }}>
                                        {field.label}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {field.options.map((opt) => (
                                            <Chip
                                                key={opt}
                                                label={`${opt}x`}
                                                size="small"
                                                onClick={() => handleChange(field.key, opt)}
                                                sx={{
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.7rem',
                                                    backgroundColor: values[field.key] === opt
                                                        ? `${vortxColors.gold}33`
                                                        : `${vortxColors.teal}22`,
                                                    color: values[field.key] === opt
                                                        ? vortxColors.gold
                                                        : retroColors.muted,
                                                    border: `1px solid ${values[field.key] === opt ? vortxColors.gold : `${vortxColors.teal}44`}`,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: `${vortxColors.gold}22`,
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            );

                        case 'select':
                            return (
                                <FormControl key={field.key} size="small" sx={fieldSx}>
                                    <InputLabel>{field.label}</InputLabel>
                                    <Select
                                        value={values[field.key]}
                                        label={field.label}
                                        onChange={(e) => handleChange(field.key, e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: retroColors.surface,
                                                    border: `1px solid ${vortxColors.teal}44`,
                                                },
                                            },
                                        }}
                                    >
                                        {field.options.map((opt) => (
                                            <MenuItem
                                                key={opt.value}
                                                value={opt.value}
                                                sx={{
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.75rem',
                                                    color: vortxColors.white,
                                                    '&:hover': { backgroundColor: `${vortxColors.teal}22` },
                                                    '&.Mui-selected': { backgroundColor: `${vortxColors.gold}22` },
                                                }}
                                            >
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );

                        default:
                            return null;
                    }
                })}
            </Box>

            <Button
                onClick={handleInject}
                variant="contained"
                fullWidth
                sx={{
                    mt: 2,
                    backgroundColor: vortxColors.gold,
                    color: retroColors.bg,
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    '&:hover': {
                        backgroundColor: vortxColors.goldHover,
                    },
                }}
            >
                Inject
            </Button>
        </Popover>
    );
}

/**
 * EventInjector - Dropdown button that lets users inject events into the simulation.
 *
 * @param {{ onSend: function, disabled: boolean }} props
 */
export default function EventInjector({ onSend, disabled }) {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [popoverAnchor, setPopoverAnchor] = useState(null);

    const handleMenuOpen = (e) => {
        setMenuAnchor(e.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleEventSelect = (eventDef, e) => {
        setSelectedEvent(eventDef);
        setPopoverAnchor(e.currentTarget);
        handleMenuClose();
    };

    const handlePopoverClose = () => {
        setSelectedEvent(null);
        setPopoverAnchor(null);
    };

    const handleInject = (eventPayload) => {
        onSend('inject_event', { event: eventPayload });
    };

    return (
        <>
            <Button
                onClick={handleMenuOpen}
                disabled={disabled}
                variant="outlined"
                startIcon={<BoltOutlined />}
                sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: retroColors.accent,
                    borderColor: `${vortxColors.teal}66`,
                    '&:hover': {
                        borderColor: retroColors.accent,
                        backgroundColor: `${retroColors.accent}11`,
                    },
                    '&.Mui-disabled': {
                        color: `${retroColors.accent}44`,
                        borderColor: `${vortxColors.teal}33`,
                    },
                }}
            >
                Inject Event
            </Button>

            {/* Event Type Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        backgroundColor: retroColors.surface,
                        border: `1px solid ${vortxColors.teal}66`,
                        boxShadow: `0 0 20px ${vortxColors.teal}22`,
                    },
                }}
            >
                {EVENT_TYPES.map((eventDef) => (
                    <MenuItem
                        key={eventDef.type}
                        onClick={(e) => handleEventSelect(eventDef, e)}
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: vortxColors.white,
                            '&:hover': {
                                backgroundColor: `${vortxColors.teal}22`,
                            },
                        }}
                    >
                        {eventDef.label}
                    </MenuItem>
                ))}
            </Menu>

            {/* Event Config Popover */}
            {selectedEvent && popoverAnchor && (
                <EventConfigPopover
                    eventDef={selectedEvent}
                    anchorEl={popoverAnchor}
                    onClose={handlePopoverClose}
                    onInject={handleInject}
                />
            )}
        </>
    );
}
